import { type ISODate, addDays, daysBetween, daysSince, toISODate } from './dates';
import { plural } from './format';
import type { Task, TaskStatus } from './types';

/* ---------- Escopo e filtros ---------- */
export interface BoardScope {
  /** 'all' | 'mine' | id de projeto */
  project: string;
  /** 'all' | 'me' | 'nobody' | id de pessoa */
  person: string;
  theme: string;
  category: string;
  entity: string;
}
export const DEFAULT_SCOPE: BoardScope = { project: 'all', person: 'all', theme: 'all', category: 'all', entity: 'all' };

const hasOpenSubtaskFor = (t: Task, userId: string | null) => t.subtasks.some(s => s.assigneeId === userId && !s.done);

export function inScope(t: Task, s: BoardScope, me: string | null): boolean {
  if (s.project === 'mine') {
    if (t.assigneeId !== me && !hasOpenSubtaskFor(t, me)) return false;
  } else if (s.project !== 'all' && t.projectId !== s.project) return false;
  if (s.project !== 'mine' && s.person !== 'all') {
    if (s.person === 'nobody') { if (t.assigneeId) return false; }
    else {
      const p = s.person === 'me' ? me : s.person;
      if (t.assigneeId !== p && !hasOpenSubtaskFor(t, p)) return false;
    }
  }
  if (s.theme !== 'all' && !t.themeIds.includes(s.theme)) return false;
  if (s.category !== 'all' && t.classification?.categoryId !== s.category) return false;
  if (s.entity !== 'all' && !t.entityIds.includes(s.entity)) return false;
  return true;
}

export function matchesSearch(t: Task, q: string, normalize: (s: string) => string) {
  const n = normalize(q.trim());
  return !n || normalize(`${t.title} ${t.description} ${t.waiting?.who ?? ''}`).includes(n);
}

export type SortMode = 'manual' | 'priority' | 'due';
export function sortColumn(list: Task[], status: TaskStatus, mode: SortMode): Task[] {
  const byDue = (a: Task, b: Task) => (a.due || '9').localeCompare(b.due || '9');
  if (status === 'done') return list.sort((a, b) => (b.completedAt || '').localeCompare(a.completedAt || ''));
  if (mode === 'priority') return list.sort((a, b) => b.priority - a.priority || byDue(a, b));
  if (mode === 'due') return list.sort((a, b) => byDue(a, b) || b.priority - a.priority);
  return list.sort((a, b) => a.position - b.position);
}

/** Posição fracionária para inserir entre vizinhos (ordem manual). */
export function positionBetween(prev: number | undefined, next: number | undefined): number {
  if (prev === undefined && next === undefined) return 1000;
  if (prev === undefined) return next! - 1000;
  if (next === undefined) return prev + 1000;
  return (prev + next) / 2;
}

/** Posição para soltar `taskId` em `status`, antes de `beforeId` (ou no fim). */
export function positionFor(tasks: Task[], taskId: string, status: TaskStatus, beforeId: string | null): number {
  const list = tasks.filter(x => x.status === status && x.id !== taskId).sort((a, b) => a.position - b.position);
  let i = beforeId ? list.findIndex(x => x.id === beforeId) : list.length;
  if (i < 0) i = list.length;
  return positionBetween(list[i - 1]?.position, list[i]?.position);
}

export const endPosition = (tasks: Task[], status: TaskStatus) => {
  const same = tasks.filter(t => t.status === status);
  return same.length ? Math.max(...same.map(t => t.position)) + 1000 : 1000;
};

/* ---------- Pontos de atenção ---------- */
export interface Alert {
  id: string;
  level: 'red' | 'orange' | 'yellow' | 'purple';
  weight: number;
  title: string;
  description: string;
  taskIds: string[];
}

export function computeAlerts(tasks: Task[], opts: { today: ISODate; wipLimit: number; firstName: (id: string) => string; now?: number }): Alert[] {
  const { today: h, wipLimit: wip } = opts;
  const since = (iso: string | null) => daysSince(iso, opts.now);
  const open = tasks.filter(t => t.status !== 'done');
  const A: Alert[] = [];

  const late = open.filter(t => t.due && t.due < h);
  if (late.length) A.push({ id: 'late', level: 'red', weight: 3, title: plural(late.length, 'tarefa atrasada', 'tarefas atrasadas'), description: 'Prazo vencido e ainda não finalizadas. Renegocie o prazo ou priorize hoje.', taskIds: late.map(t => t.id) });

  const followUp = open.filter(t => t.status === 'waiting' && t.waiting?.followUpOn && t.waiting.followUpOn <= h);
  if (followUp.length) A.push({ id: 'follow-up', level: 'red', weight: 3, title: `Cobrar retorno de ${plural(followUp.length, 'pendência', 'pendências')}`, description: `Chegou a hora de cobrar: ${[...new Set(followUp.map(t => t.waiting!.who))].join(', ')}.`, taskIds: followUp.map(t => t.id) });

  const doing = open.filter(t => t.status === 'doing');
  if (doing.length > wip) A.push({ id: 'wip', level: 'orange', weight: 2, title: `Fazendo acima do limite (${doing.length} de ${wip})`, description: 'Muita coisa ao mesmo tempo atrasa tudo. Finalize algo antes de puxar novas tarefas.', taskIds: doing.map(t => t.id) });

  const stalled = doing.filter(t => since(t.enteredStatusAt) >= 5);
  if (stalled.length) A.push({ id: 'stalled', level: 'orange', weight: 2, title: plural(stalled.length, 'tarefa parada', 'tarefas paradas') + ' em Fazendo', description: 'Sem mudar de etapa há 5 dias ou mais. Pode haver bloqueio, ou vale quebrar em partes menores.', taskIds: stalled.map(t => t.id) });

  const byPerson = new Map<string, string[]>();
  doing.forEach(t => { if (t.assigneeId) byPerson.set(t.assigneeId, [...(byPerson.get(t.assigneeId) ?? []), t.id]); });
  byPerson.forEach((ids, u) => {
    if (ids.length >= 3) A.push({ id: 'load-' + u, level: 'orange', weight: 2, title: `${opts.firstName(u)} com ${ids.length} tarefas em andamento`, description: 'Risco de gargalo. Redistribua ou defina o que pode esperar.', taskIds: ids });
  });

  const unassigned = open.filter(t => (t.status === 'todo' || t.status === 'doing') && !t.assigneeId);
  if (unassigned.length) A.push({ id: 'unassigned', level: 'yellow', weight: 1, title: plural(unassigned.length, 'tarefa sem responsável', 'tarefas sem responsável'), description: 'Já priorizadas, mas ninguém assumiu.', taskIds: unassigned.map(t => t.id) });

  const dueSoon = open.filter(t => t.due && t.due >= h && daysBetween(h, t.due) <= 2 && t.status !== 'doing' && t.status !== 'waiting');
  if (dueSoon.length) A.push({ id: 'due-soon', level: 'yellow', weight: 1, title: `${plural(dueSoon.length, 'prazo', 'prazos')} em até 2 dias sem começar`, description: 'Ainda não estão em Fazendo. Comece agora para não atrasar.', taskIds: dueSoon.map(t => t.id) });

  const lateSubs = open.filter(t => t.subtasks.some(s => !s.done && s.due && s.due < h));
  if (lateSubs.length) {
    const n = lateSubs.reduce((k, t) => k + t.subtasks.filter(s => !s.done && s.due && s.due < h).length, 0);
    A.push({ id: 'late-subtasks', level: 'red', weight: 2, title: plural(n, 'subtarefa atrasada', 'subtarefas atrasadas'), description: 'Partes de tarefas com prazo vencido. Podem estar segurando a entrega principal.', taskIds: lateSubs.map(t => t.id) });
  }

  const forgotten = open.filter(t => t.status === 'mapped' && since(t.enteredStatusAt) >= 14);
  if (forgotten.length) A.push({ id: 'forgotten', level: 'purple', weight: 1, title: plural(forgotten.length, 'demanda mapeada', 'demandas mapeadas') + ' há mais de 2 semanas', description: 'Decida: priorizar, agendar ou descartar.', taskIds: forgotten.map(t => t.id) });

  return A.sort((a, b) => b.weight - a.weight);
}

/* ---------- Ritmo ---------- */
export interface Metrics {
  doneLast7: number;
  avgCycleDays: number | null;
  doing: number;
  waiting: number;
  /** entregas por dia, 14 dias, o último é hoje */
  perDay: number[];
}
export function computeMetrics(tasks: Task[], today: ISODate, now = Date.now()): Metrics {
  const done = tasks.filter(t => t.status === 'done' && t.completedAt);
  const cycles = done
    .filter(t => daysSince(t.completedAt, now) < 30 && t.startedAt)
    .map(t => Math.max(0.5, (new Date(t.completedAt!).getTime() - new Date(t.startedAt!).getTime()) / 864e5));
  return {
    doneLast7: done.filter(t => daysSince(t.completedAt, now) < 7).length,
    avgCycleDays: cycles.length ? cycles.reduce((a, b) => a + b, 0) / cycles.length : null,
    doing: tasks.filter(t => t.status === 'doing').length,
    waiting: tasks.filter(t => t.status === 'waiting').length,
    perDay: [...Array(14)].map((_, i) => {
      const s = addDays(today, i - 13);
      return done.filter(t => toISODate(new Date(t.completedAt!)) === s).length;
    }),
  };
}

/* ---------- Transição de etapa ---------- */
/**
 * Calcula os campos derivados quando uma tarefa muda de etapa.
 * Usado no servidor (fonte da verdade) e no cliente (atualização otimista).
 */
export function transition(t: Pick<Task, 'status' | 'startedAt' | 'createdAt' | 'waiting'>, to: TaskStatus, nowIso: string) {
  const patch: Partial<Task> = {};
  if (t.status === to) return patch;
  patch.status = to;
  patch.enteredStatusAt = nowIso;
  if (to === 'doing' && !t.startedAt) patch.startedAt = nowIso;
  if (to === 'done') {
    patch.completedAt = nowIso;
    if (!t.startedAt) patch.startedAt = t.createdAt;
  } else patch.completedAt = null;
  if (to !== 'waiting') patch.waiting = null;
  return patch;
}
